<template lang="pug">
	.audio-tracker-recorder
		.flex-row.full-width.center
			.flex-col.flex-1
				.flex-col.space-large.flex-1
					.font-big.upper.grey.text-center(v-if="showHeader") {{$tCaption('Recorder')}}
					.flex-col
						DxTextBox.flex-1(v-if="showTitle", :value.sync="title", :placeholder="$tCaption('Title')")
						.tb-padding(v-if="$scopedSlots['top']")
							slot(name="top")
						.flex-row.space-large
							.flex-row.space.align-center(v-if="!hideAudioTools")
								DxCheckBox(:value.sync="fileUploading.trim", :text="$tCaption('Trim')")
								DxNumberBox(:value.sync="fileUploading.trimDbLevel", width="45", format="#0.## dB", :disabled="!fileUploading.trim")
							.flex-row.space.align-center(v-if="!hideAudioTools")
								DxCheckBox(:value.sync="fileUploading.normalize", :text="$tCaption('Normalize')")
								DxNumberBox(:value.sync="fileUploading.normalizeDbLevel", width="45", format="#0.## dB", :disabled="!fileUploading.normalize")
							.flex-row.space.align-center(v-if="!readonly")
								DxCheckBox(:value.sync="listeningMic.value", :text="$tCaption('Mic preview')")
					.flex-row.space.align-center(v-if="!$scopedSlots['actions']")
						DxButton.flex-1(v-if="!readonly", :text="recorder.isRecording ? $tCaption('Re-take') : $tCaption('Record')", :icon="recorder.isRecording ? 'fas fa-redo' : 'isnotblank'" type="danger", @click="v => startRecording()")
						DxButton.flex-1(v-if="!readonly", :text="$tCaption('Stop', '@actions')", icon="fas fa-stop" type="normal", @click="onStopClick", :disabled="!recorder.isRecording")
						slot(name="actions-end")
					slot(name="actions")
					.metering
						VuMeter(v-if="!readonly", ref="vumeter", :tracker="tracker", :track="track", :isPlaying="recorder.isListening", :filters="{ analyzer, stereoAnalyser }", @change="onLevelChange")
						.max-peak.flex-row.space.align-center 
							.grey {{ $tCaption('Peak') }}:
							.flex-row.align-center
								span {{ $Data.formatNumber(peak, '#0.#') }}
								span {{ 'dB' }}
					.flex-row.align-center.center(v-if="!readonly")
						.recorder-time(:class="recorderClass") {{duration}}
				DxScrollView(v-if="recorder.speakingText", :height="200", showScrollbar="always")
					.text-area.flex-col.align-center.center
						.font-large.accent-color {{recorder.speakingText}}
				slot(name="bottom")
			AudioTrackerRecorderCounter(v-if="autoStart", ref="counter")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import DxButton from 'devextreme-vue/button';
	import Loader from 'app-core/Loader.vue';
	import { DxSlider } from 'devextreme-vue/slider';
	import DxTextBox from 'devextreme-vue/text-box';
	import Recorder from 'recorder/recorder';
	import Promise from 'bluebird';
	import VuMeter from './AudioTrackerVUMeter.vue';
	import moment from 'moment';
	import recorderWorker from 'worker-loader!recorder/recorderWorker.js';
	import audioHelper from './audio-helper';
	import AudioTrackerRecorderCounter from './AudioTrackerRecorderCounter.vue';
	import DxNumberBox from 'devextreme-vue/number-box';
	import DxCheckBox from 'devextreme-vue/check-box';
	import storage from 'storage';
	import DxScrollView from 'devextreme-vue/scroll-view';

	export default {
		translationCategory: 'audioTracker.AudioTrackerRecorder',
		name: 'audio-tracker-recorder',
		components: {DxButton, Loader, DxSlider, VuMeter, DxTextBox, AudioTrackerRecorderCounter, DxNumberBox, DxCheckBox, DxScrollView},
		props: {
			track: VueTypes.any.def(null),
			tracker: VueTypes.any.def(null),
			audioContext: VueTypes.any.def(null),
			disabled: VueTypes.bool.def(false),
			opts: VueTypes.any.def(null),
			showTitle: VueTypes.bool.def(true),
			showHeader: VueTypes.bool.def(true),
			hideAudioTools: VueTypes.bool.def(false),
			readonly: VueTypes.bool.def(false)
		},
		data() {
			return {
				title: null,
				recorder: {
					initiated: false,
					isRecording: false,
					isListening: false,
					stream: null,
					instance: null,
					resolve: null,
					resultWav: null,
					resultBlob: null,
					startTime: null,
					stopTime: null,
					interval: null,
					duration: 0,
					audioContext: null,
					isPaused: false,
					trackerInitPosition: null,
					countdownTime: 0,
					speakingText: null,
					opts: null,
					init: null
				},
				analyzer: null,
				stereoAnalyser: null,
				fileUploadingProp: storage.prop('audioTrackerRecorder_trimAndNormalize', void 0, {
					trim: true,
					trimDbLevel: -40,
					normalize: false,
					normalizeDbLevel: -3
				}),
				listeningMic: storage.prop('listeningMic', void 0, true),
				autoStart: false,
				micIsAllowed: true,
				peak: 0
			};
		},
		computed: {
			countdownTime() {
				return this.recorder.countdownTime && this.recorder.duration <= this.recorder.countdownTime ?  (this.recorder.countdownTime - this.recorder.duration) : 0;
			},
			duration() {
				const duration = this.recorder.countdownTime && this.recorder.duration <= this.recorder.countdownTime ? (this.recorder.countdownTime - this.recorder.duration) : (this.recorder.duration - this.recorder.countdownTime);
				return moment(duration).format('mm:ss.S');
			},
			fileUploading: {
				get: function() { 
					return this.fileUploadingProp.value; 
				},
				set: function(v) { 
					return _.assign(this.fileUploadingProp.value, v); 
				}
			},
			recorderClass() {
				return { 'red': this.countdownTime && this.countdownTime <= 5000, 'countdown': this.countdownTime };
			}
		},
		watch: {
			opts(v) {
				this.init(v);
			},
			'recorder.isRecording'(v) {
				this.$emit('is-recording', v);
			},
			'listeningMic.value'() {
				this.init(this.opts);
			}
		},
		mounted() {
			if (__DEBUG__) {
				window.recorder = this;
				window.recorderWorker = recorderWorker;
				window.moment = moment;
				window.Recorder = Recorder;
			}

			this.init(this.opts);
			this.calcMicState();
		},
		destroyed() {
			this.stopRecording(true);
		},
		methods: {
			onLevelChange(v) {
				this.peak = v?.peakDbLevelL || 0; 
			},
			async init(opts) {
				const trackPosition = opts?.trackerPosition || this.tracker?.lastTime;
				await this.stopRecording(true);
				const recorder = {};
				recorder.id = this.$Data.uuid();
				recorder.initiated = false;
				recorder.duration = 0;
				recorder.resultWav = null;
				recorder.resultBlob = null;
				recorder.isPaused = false;
				recorder.isRecording = false;
				recorder.instance = null;
				recorder.trackerInitPosition = trackPosition;
				recorder.countdownTime = opts?.countdownTime * 1000 || 0;
				recorder.speakingText = opts?.speakingText;
				recorder.opts = opts;
				this.title = opts?.title || this.title || this.$tCaption('New recording');				
				this.$set(this, 'recorder', recorder);
				await this.initRecorder(recorder);

				this.autoStart = false;
				setImmediate(() => {
					if (opts?.autoStart) {
						this.autoStart = true;
						setImmediate(() => {
							this.$refs.counter.start(opts?.autoStart, () => {
								this.startRecording(recorder);
							});
						});
					}
				});
			},
			calcDuration(recorder) {
				if (!recorder.isRecording) return;
				var startTime = recorder.startTime;
				var endTime = recorder.endTime || moment();
				if (!recorder.isPaused)
					recorder.duration += endTime.toDate() - startTime.toDate();
				recorder.startTime = moment();
			},
			async initRecorder(recorder) {
				recorder.init = new Promise(resolve => {
					var constraints = { audio: {
						mandatory: {
							googEchoCancellation: false
						},
						optional: []
					}, video: false };
					return navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
						recorder.stream = stream;
						if (!recorder.audioContext)
							recorder.audioContext = new AudioContext();
						var ctx = recorder.audioContext;
						var input = ctx.createMediaStreamSource(stream);
						var analyserNode = ctx.createAnalyser();
						var sampleSize = 512;
						analyserNode.fftSize = sampleSize;

						/* Stereo analyser */
						var stereoAnalyser = new audioHelper.StereoAnalyserNode(ctx);
						stereoAnalyser.fftSize = sampleSize;
						this.stereoAnalyser = stereoAnalyser;

						const lowpassFilter = ctx.createBiquadFilter();
						lowpassFilter.type = 'lowpass';
						lowpassFilter.frequency.value = 300; // Set the cutoff frequency

						if (this.listeningMic.value) {
							input.connect(lowpassFilter);
							input.connect(ctx.destination);
							lowpassFilter.connect(analyserNode);
							analyserNode.connect(stereoAnalyser);
							stereoAnalyser.connect(ctx.destination);
						} else {
							input.connect(lowpassFilter);
							lowpassFilter.connect(analyserNode);
							analyserNode.connect(stereoAnalyser);
						}

						this.analyzer = analyserNode;

						recorder.instance = new Recorder(input, { numChannels: 1, bufferLen: this.$globals.recorderJs.bufferLen }, recorderWorker);
						recorder.instance.record();

						recorder.isListening = true;

						setImmediate(() => {
							resolve();
						});
					}).catch((ex) => {
						resolve();
						throw(ex);
					});
				});

				return recorder.init;
			},
			muteTrack(v) {
				if (this.track)
					this.track.__aggregatedTrack.muted = v;
			},
			async calcMicState() {
				const checkState = (result) => {
					if (__DEBUG__)
						console.debug(`Mic state`, { result })
					this.micIsAllowed = result?.state === 'granted';
					if (!this.micIsAllowed) {
						this.$notifyWarning(this.$tCaption('Please grant access to microphone to use this feature.'))
					}
					return this.micIsAllowed;
				}

				return navigator.permissions.query({
					name: 'microphone'
				}).then(r => {
					return checkState(r);
				});
			},
			async startRecording(recorder) {
				recorder = recorder || this.recorder;
				if (recorder.instance) {
					await this.stopRecording(true, recorder);
				}

				if (recorder.instance) {
					recorder.instance.clear();
				}
				recorder.resultWav = null;
				recorder.resultBlob = null;
				recorder.initiated = true;
				recorder.startTime = moment();
				this.muteTrack(true);
				if (recorder.endTime) {
					recorder.endTime = null;
					recorder.duration = 0;
				}

				if (__DEBUG__)
					console.debug('Start recording', {  recorder });

				return this.initRecorder(recorder).then(async () => {
					await this.calcMicState();
					if (!this.micIsAllowed) return;
					if (this.tracker) {
						this.tracker.play(recorder.trackerInitPosition + recorder.duration / 1000);
					}
					recorder.interval = setInterval(() => {
						if (this._isDestroyed) return;
						this.calcDuration(recorder);
					}, 40);
					recorder.isRecording = true;
				});
			},
			pauseRecording(recorder){
				recorder = recorder || this.recorder;
				if (!recorder.instance) return;
				if (!recorder.isPaused) {
					if (this.tracker) {
						this.tracker.stop();
					}
					this.calcDuration(recorder);
					recorder.instance.stop();
					recorder.stream.getAudioTracks()[0].enabled = false;
					this.calcWav();
				} else {
					recorder.resultWav = null;
					recorder.resultBlob = null;
					recorder.stream.getAudioTracks()[0].enabled = true;
					if (this.tracker) {
						this.tracker.play(recorder.trackerInitPosition + recorder.duration / 1000);
					}
					recorder.instance.record();
				}
				recorder.isPaused = !recorder.isPaused;
			},
			async stopRecording(preventWavGenerating, recorder) {
				recorder = recorder || this.recorder;

				if (recorder?.init)
					await recorder.init;

				recorder.trackerTime = 0;
				if (this.tracker) {
					this.tracker.stop(true);
					this.tracker.currentTime = recorder.trackerInitPosition;
					this.tracker.lastTime = recorder.trackerInitPosition;
				}
				if (!recorder.instance) return Promise.resolve(null);
				if (recorder.interval)
					clearInterval(recorder.interval);
				recorder.isRecording = false;
				recorder.isListening = false;
				recorder.endTime = moment();
				recorder.stream.getAudioTracks()[0].stop();
				recorder.instance.stop();
				this.muteTrack(false);
				if (__DEBUG__)
					console.debug('Stop recording', { resolver: recorder.resolve, recorder });
				return preventWavGenerating ? Promise.resolve(null) : this.calcWav(recorder);
			},
			calcWav(recorder) {
				return this.getRecordingWav(recorder).then(({ base64, blob }) => {
					if (__DEBUG__)
						console.debug('Record wav generated', { base64, blob });
					recorder.resultWav = base64;
					recorder.resultBlob = blob;
					this.$emit('result-wav', base64);
				});
			},
			getRecordingWav(recorder) {
				if (!recorder.instance) return Promise.resolve(null);
				return new Promise(resolve => recorder.instance.exportWAV((blob) => {
					var fr = new FileReader();
					fr.onloadend = () => {
						if (__DEBUG__)
							console.log('Wav exported', { blob, base64: fr.result });
						resolve({ base64: fr.result, blob });
					};
					fr.readAsDataURL(blob);
				}));
			},
			onStopClick() {
				this.stopRecording();
				this.initRecorder(this.recorder);
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.audio-tracker-recorder {		
		position: relative;

		.recorder-time {
			font-size: 50px;
			font-weight: 100;
			transition: 0.3s;

			&.countdown.red {
				color: #f34d4d;
			}
		}

		.text-area {
			min-height: 180px;
		}

		.metering {
			position: relative;

			.max-peak {
				position: absolute;
				right: 2px;
				bottom: -18px;
				font-size: 11px;
			}
		}
	}
</style>
